import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import { useWeekStops } from './useWeekStops'
import { buildDayGroups, buildDayStats } from './weekDerivations'
import { getCrewColor } from '../crewColors'
import { parseYmd, getWeekTypeForDate } from '../scheduleUtils'
import WeekTraceLayer from './WeekTraceLayer'
import WeekDensityLayer from './WeekDensityLayer'
import WeekGridView from './WeekGridView'
import WeekDayLegend from './WeekDayLegend'
import WeekCrewLegend from './WeekCrewLegend'
import WeekToolbar from './WeekToolbar'
import WeekViewModeToggle from './WeekViewModeToggle'
import WeekSidebar from './WeekSidebar'
import WeekABFilter from './WeekABFilter'

const WeekVizContext = createContext(null)

/**
 * Provider that owns the shared state for WeekViz UI (data fetch, color mode,
 * activeDay/hoverDay).  Mount this once in MapTab around the JSX subtree that
 * needs it.  Render the two visual pieces via `<WeekVizOverlays>` (positioned
 * absolutely inside the map area) and `<WeekVizSidebar>` (sibling of the map
 * area).  Both consume the same context.
 */
export function WeekVizProvider ({
  mapRef, url, focusDate, focusCrewId, isFleetMode, planningMode,
  availableCrews, onSnackbar, children
}) {
  // Initial colorMode from scope (sticky thereafter — spec §6.3).
  const [colorMode, setColorMode] = useState(
    () => isFleetMode ? 'crew' : 'day'
  )
  const [activeDay, setActiveDay] = useState(null)
  const [hoverDay, setHoverDay] = useState(null)
  const [vizStyle, setVizStyle] = useState('trace')
  const [weekSel, setWeekSel] = useState('both')

  // When entering density mode, force colorMode='day'; restore on exit.
  const prevColorModeRef = useRef(colorMode)
  useEffect(() => {
    if (vizStyle === 'density') {
      prevColorModeRef.current = colorMode
      setColorMode('day')
    } else if (prevColorModeRef.current) {
      setColorMode(prevColorModeRef.current)
    }
  }, [vizStyle])

  const { stops: rawStops, days: rawDays, missingDates, missingByCrew, loading, error, yard, refetch } = useWeekStops({
    url, focusDate, planningMode, focusCrewId, isFleetMode, availableCrews
  })

  // Surface fetch errors via the existing snackbar (spec §12).
  useEffect(() => {
    if (error && onSnackbar) onSnackbar(error, 'error')
  }, [error, onSnackbar])

  // A/B week filter — active only in two-week planning mode.
  const filteredStops = useMemo(() => {
    if (planningMode !== 'two-week' || weekSel === 'both') return rawStops
    if (!focusDate) return rawStops
    const mondayThis = parseYmd(focusDate)
    const dow = mondayThis.getDay()
    mondayThis.setDate(mondayThis.getDate() + (dow === 0 ? -6 : 1 - dow))
    const firstWeekType = getWeekTypeForDate(mondayThis)
    const wantType = weekSel === 'a' ? firstWeekType : (firstWeekType === 'A' ? 'B' : 'A')
    return rawStops.filter(s => s.weekType === wantType)
  }, [rawStops, planningMode, weekSel, focusDate])

  const filteredDays = useMemo(() => {
    if (planningMode !== 'two-week' || weekSel === 'both') return rawDays
    if (!focusDate) return rawDays
    const mondayThis = parseYmd(focusDate)
    const dow = mondayThis.getDay()
    mondayThis.setDate(mondayThis.getDate() + (dow === 0 ? -6 : 1 - dow))
    const firstWeekType = getWeekTypeForDate(mondayThis)
    const wantType = weekSel === 'a' ? firstWeekType : (firstWeekType === 'A' ? 'B' : 'A')
    return rawDays.filter(d => d.weekType === wantType)
  }, [rawDays, planningMode, weekSel, focusDate])

  const stops = filteredStops
  const days = filteredDays

  // Crew color lookup keyed by string crewId.
  const crewColors = useMemo(() => {
    const ids = (availableCrews || []).map(c => String(c.crewId))
    const out = {}
    for (const c of availableCrews || []) {
      out[String(c.crewId)] = getCrewColor(String(c.crewId), ids)
    }
    return out
  }, [availableCrews])

  // Active crews currently appearing in `stops` (for the crew legend).
  const activeCrews = useMemo(() => {
    const seen = new Map()
    for (const s of stops) {
      if (!seen.has(s.crewId)) {
        seen.set(s.crewId, { crewId: s.crewId, crewName: s.crewName })
      }
    }
    return Array.from(seen.values())
  }, [stops])

  const dayGroups = useMemo(() => buildDayGroups(stops, days), [stops, days])
  const dayStats = useMemo(() => buildDayStats(stops), [stops])

  const value = useMemo(() => ({
    mapRef, focusDate, focusCrewId, isFleetMode, availableCrews,
    planningMode,
    stops, days, missingDates, missingByCrew, loading, error, yard, refetch,
    colorMode, setColorMode,
    activeDay, setActiveDay,
    hoverDay, setHoverDay,
    vizStyle, setVizStyle,
    weekSel, setWeekSel,
    crewColors, activeCrews, dayGroups, dayStats
  }), [
    mapRef, focusDate, focusCrewId, isFleetMode, availableCrews,
    planningMode,
    stops, days, missingDates, missingByCrew, loading, error, yard, refetch,
    colorMode, activeDay, hoverDay, vizStyle,
    weekSel,
    crewColors, activeCrews, dayGroups, dayStats
  ])

  return <WeekVizContext.Provider value={value}>{children}</WeekVizContext.Provider>
}

export function useWeekVizContext () {
  const ctx = useContext(WeekVizContext)
  if (!ctx) throw new Error('useWeekVizContext must be used within WeekVizProvider')
  return ctx
}

/**
 * Renders the map-area overlays: trace layer (Leaflet polylines + markers),
 * top-center day legend, top-right color toggle, bottom-left crew legend,
 * loading indicator, and the inline error pane.
 * MUST be rendered inside the same map area `<div>` that owns the Leaflet
 * map (so absolutely-positioned overlays land in the right place).
 */
export function WeekVizOverlays () {
  const {
    mapRef, error, loading, refetch, yard,
    planningMode, focusDate,
    dayGroups, stops, colorMode, setColorMode, crewColors, activeCrews,
    activeDay, setActiveDay, hoverDay, setHoverDay,
    vizStyle, setVizStyle,
    weekSel, setWeekSel,
    dayStats
  } = useWeekVizContext()

  if (error) {
    return (
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 12, color: '#a9341d',
        background: 'rgba(251,248,241,0.85)', zIndex: 1000
      }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Couldn't load week schedule</div>
        <div style={{ fontSize: 12, color: '#6f7a6b' }}>{error}</div>
        <button
          onClick={refetch}
          style={{
            padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: '#1f5e3a', color: '#fff', fontSize: 12, fontWeight: 600
          }}
        >Retry</button>
      </div>
    )
  }

  return (
    <>
      {loading && (
        <div style={{
          position: 'absolute', top: 14, right: 14, zIndex: 1000,
          background: 'rgba(251,248,241,0.94)', borderRadius: 999, padding: '6px 10px',
          display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
        }}>
          <CircularProgress size={14} />
          <span style={{ fontSize: 11.5, fontWeight: 600, color: '#3d463a' }}>Loading week…</span>
        </div>
      )}

      {vizStyle === 'grid' && (
        <WeekGridView
          dayGroups={dayGroups}
          yard={yard}
          colorMode={colorMode}
          crewColors={crewColors}
        />
      )}

      {vizStyle === 'trace' && (
        <WeekTraceLayer
          mapRef={mapRef}
          dayGroups={dayGroups}
          yard={yard}
          colorMode={colorMode}
          crewColors={crewColors}
          activeDay={activeDay}
          hoverDay={hoverDay}
        />
      )}

      {vizStyle === 'density' && (
        <WeekDensityLayer
          mapRef={mapRef}
          stops={stops}
          activeDay={activeDay}
          hoverDay={hoverDay}
        />
      )}

      {vizStyle !== 'grid' && (
        <div style={{
          position: 'absolute', top: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 1000
        }}>
          <WeekDayLegend
            dayStats={dayStats}
            activeDay={activeDay} setActiveDay={setActiveDay}
            hoverDay={hoverDay} setHoverDay={setHoverDay}
          />
        </div>
      )}

      <div style={{
        position: 'absolute', top: 14, right: 14, zIndex: 1000,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6
      }}>
        <WeekViewModeToggle vizStyle={vizStyle} setVizStyle={setVizStyle} />
        <div
          title={vizStyle === 'density' ? 'Density uses day colors' : undefined}
          style={vizStyle === 'density' ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
        >
          <WeekToolbar colorMode={colorMode} setColorMode={setColorMode} />
        </div>
        {planningMode === 'two-week' && (
          <WeekABFilter
            focusDate={focusDate}
            weekSel={weekSel}
            setWeekSel={setWeekSel}
          />
        )}
      </div>

      {colorMode === 'crew' && vizStyle !== 'density' && (
        <div style={{ position: 'absolute', bottom: 16, left: 16, zIndex: 1000 }}>
          <WeekCrewLegend activeCrews={activeCrews} crewColors={crewColors} />
        </div>
      )}
    </>
  )
}

/** Sidebar piece — render as a sibling of the map area. */
export function WeekVizSidebar () {
  const {
    focusDate, focusCrewId, isFleetMode, availableCrews,
    days, missingDates, missingByCrew,
    activeDay, setActiveDay, hoverDay, setHoverDay
  } = useWeekVizContext()
  return (
    <WeekSidebar
      focusDate={focusDate}
      focusCrewId={focusCrewId}
      isFleetMode={isFleetMode}
      availableCrews={availableCrews}
      days={days}
      activeDay={activeDay} setActiveDay={setActiveDay}
      hoverDay={hoverDay} setHoverDay={setHoverDay}
      missingDates={missingDates}
      missingByCrew={missingByCrew}
    />
  )
}

/**
 * Back-compat default export.  Keeps existing tests passing by rendering
 * both pieces as siblings in a self-contained flex container.  Production
 * callers (MapTab) should use the split form via `<WeekVizProvider>` +
 * `<WeekVizOverlays>` and `<WeekVizSidebar>` rendered at the right
 * structural locations.
 */
export default function WeekViz (props) {
  return (
    <WeekVizProvider {...props}>
      <div style={{ flex: 1, display: 'flex', position: 'relative', minHeight: 0 }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <WeekVizOverlays />
        </div>
        <WeekVizSidebar />
      </div>
    </WeekVizProvider>
  )
}
